import React, { useState, useRef, useEffect } from 'react';
import { Search, Filter, ChevronDown, ChevronRight, Users, X } from 'lucide-react';
import { cn } from '../../lib/utils';

const LEVEL_COLORS = {
  0: '#2f5be8', 1: '#4a72f0', 2: '#0ab394', 3: '#6340d4',
  4: '#1a9ec0', 5: '#4a9020', 6: '#9098c0',
};
const LEVEL_NAMES = ['Nivel 0','Nivel 1','Nivel 2','Nivel 3','Nivel 4','Nivel 5','Nivel 6'];

export const OrgChartToolbar = ({
  activeFilter, onFilterChange, onSearch, isEditMode, onToggleEdit, onAddNode, userRole, nodes,
}) => {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showDirectory, setShowDirectory] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (text) => {
    setSearchText(text);
    if (text.trim().length < 2) { setSearchResults([]); setShowResults(false); return; }
    const q = text.toLowerCase();
    const results = (nodes || [])
      .filter(n => n.puesto.toLowerCase().includes(q) || n.nombre.toLowerCase().includes(q))
      .slice(0, 8);
    setSearchResults(results);
    setShowResults(results.length > 0);
  };

  const handleSelectResult = (nodeId) => {
    onSearch(nodeId);
    setSearchText('');
    setShowResults(false);
  };

  const vacantCount = (nodes || []).filter(n => n.nombre === 'Vacante').length;

  // Group nodes by level for directory
  const levelGroups = {};
  (nodes || []).forEach(n => {
    if (!levelGroups[n.level]) levelGroups[n.level] = [];
    levelGroups[n.level].push(n);
  });

  return (
    <div className="flex flex-col gap-2" data-testid="orgchart-toolbar">
      {/* Edit mode banner */}
      {isEditMode && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-2 flex items-center gap-2 animate-pulse">
          <span className="text-amber-700 text-sm font-semibold">Modo Edicion Activo</span>
          <span className="text-amber-600 text-xs">Haz clic en un nodo para editarlo</span>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {/* Search */}
        <div className="relative" ref={searchRef}>
          <div className="flex items-center bg-white border border-slate-200 rounded-lg px-3 py-1.5 w-64 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
            <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              type="text"
              value={searchText}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="Buscar por nombre o puesto..."
              className="ml-2 flex-1 bg-transparent outline-none text-sm text-slate-700 placeholder-slate-400"
              data-testid="orgchart-search-input"
            />
            {searchText && (
              <button onClick={() => { setSearchText(''); setShowResults(false); }} className="p-0.5">
                <X className="w-3.5 h-3.5 text-slate-400" />
              </button>
            )}
          </div>
          {showResults && (
            <div className="absolute top-full mt-1 left-0 w-80 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto" data-testid="orgchart-search-results">
              {searchResults.map(n => (
                <button
                  key={n.node_id}
                  onClick={() => handleSelectResult(n.node_id)}
                  className="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-slate-50 text-left border-b border-slate-50 last:border-0"
                >
                  <div className="w-7 h-7 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: n.nombre === 'Vacante' ? '#d93050' : (LEVEL_COLORS[n.level] || '#9098c0') }}>
                    {n.nombre === 'Vacante' ? 'V' : n.nombre.split(/[\s/]+/)[0]?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{n.puesto}</p>
                    <p className="text-xs text-slate-500 truncate">{n.nombre}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Separator */}
        <div className="h-6 w-px bg-slate-200" />

        {/* Level filters */}
        <div className="flex items-center gap-1">
          <Filter className="w-4 h-4 text-slate-400 mr-1" />
          {[0,1,2,3,4,5,6].map(lvl => (
            <button
              key={lvl}
              onClick={() => onFilterChange(activeFilter === lvl ? null : lvl)}
              className={cn(
                'px-2 py-1 rounded text-xs font-medium transition-all',
                activeFilter === lvl
                  ? 'text-white shadow-sm'
                  : 'text-slate-600 bg-white border border-slate-200 hover:border-slate-300'
              )}
              style={activeFilter === lvl ? { backgroundColor: LEVEL_COLORS[lvl] } : {}}
              data-testid={`orgchart-filter-level-${lvl}`}
            >
              N{lvl}
            </button>
          ))}
          <button
            onClick={() => onFilterChange(activeFilter === 'vacant' ? null : 'vacant')}
            className={cn(
              'px-2 py-1 rounded text-xs font-medium transition-all ml-1',
              activeFilter === 'vacant'
                ? 'bg-red-500 text-white shadow-sm'
                : 'text-slate-600 bg-white border border-slate-200 hover:border-slate-300'
            )}
            data-testid="orgchart-filter-vacant"
          >
            Vacantes ({vacantCount})
          </button>
          {activeFilter !== null && (
            <button
              onClick={() => onFilterChange(null)}
              className="px-2 py-1 rounded text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              data-testid="orgchart-filter-clear"
            >
              Limpiar
            </button>
          )}
        </div>

        {/* Separator */}
        <div className="h-6 w-px bg-slate-200" />

        {/* Directory toggle */}
        <button
          onClick={() => setShowDirectory(!showDirectory)}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
            showDirectory ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
          )}
          data-testid="orgchart-directory-toggle"
        >
          <Users className="w-3.5 h-3.5" />
          Directorio
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Admin: Edit mode + Add */}
        {userRole === 'admin' && (
          <div className="flex items-center gap-2">
            {isEditMode && (
              <button
                onClick={onAddNode}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                data-testid="orgchart-add-node"
              >
                + Agregar
              </button>
            )}
            <button
              onClick={onToggleEdit}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                isEditMode ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
              )}
              data-testid="orgchart-edit-toggle"
            >
              {isEditMode ? 'Salir de Edicion' : 'Editar'}
            </button>
          </div>
        )}
      </div>

      {/* Floating directory */}
      {showDirectory && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 max-h-64 overflow-y-auto" data-testid="orgchart-directory">
          {Object.keys(levelGroups).sort((a,b) => a - b).map(lvl => (
            <DirectoryLevel
              key={lvl}
              level={parseInt(lvl)}
              nodes={levelGroups[lvl]}
              onSelect={handleSelectResult}
              onFilter={() => onFilterChange(activeFilter === parseInt(lvl) ? null : parseInt(lvl))}
              isFiltered={activeFilter === parseInt(lvl)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DirectoryLevel = ({ level, nodes, onSelect, onFilter, isFiltered }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mb-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-slate-50 text-left"
      >
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: LEVEL_COLORS[level] || '#9098c0' }} />
        <span className="text-xs font-medium text-slate-700">{LEVEL_NAMES[level]} ({nodes.length})</span>
        <button
          onClick={e => { e.stopPropagation(); onFilter(); }}
          className={cn('ml-auto text-xs px-1.5 py-0.5 rounded', isFiltered ? 'bg-blue-100 text-blue-700' : 'text-slate-400 hover:text-slate-600')}
        >
          Filtrar
        </button>
      </button>
      {expanded && (
        <div className="ml-6 space-y-0.5">
          {nodes.map(n => (
            <button
              key={n.node_id}
              onClick={() => onSelect(n.node_id)}
              className="block w-full text-left px-2 py-1 text-xs rounded hover:bg-slate-50 truncate"
            >
              <span className="font-medium text-slate-700">{n.puesto}</span>
              <span className={cn("ml-1.5", n.nombre === 'Vacante' ? 'text-red-500' : 'text-slate-400')}>{n.nombre}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
